import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Plus, RefreshCw, Search, ShieldCheck, PlayCircle, CheckCheck, ToggleLeft, ToggleRight } from "lucide-react";
import { registryApi, Button, Input, Pill, Metric, ErrorNotice, Empty, date } from "./shared";
import { CandidateForm } from "./CandidateForm";
import { CoverageView, HistoryView } from "./CoverageView";
import { SourceDetail } from "./SourceDetail";
import { useAdminCtx } from "../AdminShell";
import "./registry.css";

const GRADE_COLOR = { "A+": "#16a34a", A: "#22c55e", B: "#84cc16", C: "#eab308", D: "#f97316", F: "#ef4444" };
const VERDICT_COLOR = { trusted: "#16a34a", review: "#eab308", reject: "#ef4444" };

function CgBadge({ grade, tier, verdict, score }) {
  if (!grade) return <span className="registry-cg-empty" title="Not yet scored">—</span>;
  return (
    <span className="registry-cg-badges" data-testid={`cg-badge-${grade}`}>
      <span className="registry-cg-grade" style={{ background: GRADE_COLOR[grade] || "#6b7280", color: "#fff" }} title={`CG Score: ${score ?? "—"}/100`}>{grade}</span>
      <span className="registry-cg-tier" title={`Editorial tier: ${tier}`}>{tier}</span>
      {verdict === "reject" && <span className="registry-cg-reject" title="CG Score reject — consider disabling">!</span>}
    </span>
  );
}

function ToggleBtn({ sourceId, enabled, onToggled }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (!window.confirm(enabled ? `Disable this source? It will stop being ingested.` : `Re-enable this source?`)) return;
    setBusy(true);
    try {
      await registryApi(`/admin/sources/${sourceId}/toggle`, { method: "PATCH", body: JSON.stringify({ enabled: !enabled }) });
      onToggled?.();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      className={`registry-toggle-btn ${enabled ? "registry-toggle-on" : "registry-toggle-off"}`}
      data-testid={`source-toggle-${sourceId}`}
      onClick={toggle}
      disabled={busy}
      title={enabled ? "Click to disable source" : "Click to enable source"}
      aria-label={enabled ? "Disable source" : "Enable source"}
    >
      {enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
    </button>
  );
}

export default function SourceWorkspace({ initialSearch = "", onInitSearchConsumed }) {
  const { subKey, setSubKey } = useAdminCtx() || {};
  const tab = subKey || "library";
  const setTab = (key) => { setSubKey?.(key); };

  const [data, setData] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false), [page, setPage] = useState(1), [form, setForm] = useState(null), [selected, setSelected] = useState(null), [runs, setRuns] = useState([]);
  const [filters, setFilters] = useState({ group: "", subgroup: "", country: "", search: "", review: "", method: "", health: "" });
  const [cgBusy, setCgBusy] = useState(false), [cgMsg, setCgMsg] = useState("");
  const [verdictBusy, setVerdictBusy] = useState(false), [verdictResult, setVerdictResult] = useState(null);

  useEffect(() => {
    if (!initialSearch) return;
    setFilters(s => ({ ...s, search: initialSearch }));
    setPage(1);
    onInitSearchConsumed?.();
  }, [initialSearch, onInitSearchConsumed]);

  const load = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams({ ...filters, review: tab === "review" ? "candidate" : filters.review, page });
      const d = await registryApi(`/admin/source-registry?${params}`);
      setData(d);
      if (tab === "review") setRuns((await registryApi("/admin/source-discovery")).runs);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [filters, page, tab]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useLayoutEffect(() => { document.querySelector('[data-testid="admin-sources"]')?.scrollIntoView({ block: "start", behavior: "instant" }); }, [tab]);

  const filter = (k, v) => { setPage(1); setFilters(s => ({ ...s, [k]: v, ...(k === "group" ? { subgroup: "" } : {}) })); };
  const discover = async (kind = "country_directory") => {
    if (typeof kind !== "string") kind = "country_directory";
    setBusy(true);
    try {
      await registryApi(`/admin/source-discovery?kind=${kind}`, { method: "POST" });
      setRuns((await registryApi("/admin/source-discovery")).runs);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const runCgValidation = async () => {
    setCgBusy(true); setCgMsg("");
    try {
      const r = await registryApi("/admin/sources/run-cg-validation", { method: "POST" });
      setCgMsg(r.message || "Validation started — scores will appear as sources are checked (~13s each).");
    } catch (e) { setCgMsg("Error: " + e.message); }
    finally { setCgBusy(false); }
  };

  const applyVerdicts = async () => {
    if (!window.confirm("Disable all sources with CG Score 'reject' verdict? This cannot be undone from here.")) return;
    setVerdictBusy(true);
    try {
      const r = await registryApi("/admin/sources/apply-verdicts", { method: "POST" });
      setVerdictResult(r);
      load();
    } catch (e) { alert(e.message); }
    finally { setVerdictBusy(false); }
  };

  const select = (k, v, options) => (
    <select aria-label={v} data-testid={`registry-filter-${k}`} value={filters[k]} onChange={e => filter(k, e.target.value)}>
      <option value="">{v}</option>
      {(k === "review" ? [...options, ["archived", "Archived records"]] : options).map(([kv, lv]) => <option key={kv} value={kv}>{lv}</option>)}
    </select>
  );

  return (
    <section className="registry-shell" data-testid="admin-sources">
      <div className="registry-heading">
        <div>
          <h2>Editorial source registry</h2>
          <p className="registry-note m-0">Organizations, collection endpoints & country coverage</p>
        </div>
        <div className="registry-actions">
          <Button variant="outline" disabled={busy} data-testid="registry-refresh" onClick={load}><RefreshCw size={14} />Refresh</Button>
          <Button variant="outline" disabled={cgBusy} data-testid="registry-run-cg" onClick={runCgValidation} title="Run CleverGraph CG Score on unvalidated sources (40/run, ~9 min)">
            <ShieldCheck size={14} />{cgBusy ? "Validating…" : "Run CG Score"}
          </Button>
          <Button variant="outline" disabled={verdictBusy} data-testid="registry-apply-verdicts" onClick={applyVerdicts} title="Disable all sources with CG Score 'reject' verdict">
            <CheckCheck size={14} />{verdictBusy ? "Applying…" : "Apply CG Verdicts"}
          </Button>
          <Button data-testid="registry-add-source" onClick={() => setForm({})}><Plus size={15} />Propose source</Button>
        </div>
      </div>

      {cgMsg && <p className="registry-note mt-2 text-[var(--accent)]" data-testid="cg-validation-msg">{cgMsg}</p>}
      {verdictResult && <p className="registry-note mt-2" data-testid="verdict-result">{verdictResult.disabled_count} source(s) disabled by CG verdict.</p>}

      {data && (
        <div className="registry-metrics">
          <Metric label="Organizations" value={data.stats.organizations} id="registry-organizations" note="Canonical publishing identities" />
          <Metric label="Collection endpoints" value={data.stats.endpoints} id="registry-endpoints" note="Includes candidates & retained aliases" />
          <Metric label="Active endpoints" value={data.stats.active} id="registry-active" note="Eligible for collection" />
          <Metric label="Awaiting review" value={data.stats.awaiting_review} id="registry-awaiting-review" note="Not contributing articles" />
        </div>
      )}

      <ErrorNotice error={error} />

      {tab === "coverage" ? <CoverageView onCountry={country => { filter("country", country); setTab("library"); }} /> :
       tab === "history"  ? <HistoryView /> : (
        <>
          {data && (
            <div className="registry-groups">
              {Object.entries(data.groups).map(([key, g]) => (
                <button key={key} aria-pressed={filters.group === key} data-testid={`registry-group-${key}`} onClick={() => filter("group", filters.group === key ? "" : key)}>
                  <span>{g.label}</span><strong data-testid={`registry-group-count-${key}`}>{data.stats.groups[key]}</strong>
                </button>
              ))}
            </div>
          )}

          {tab === "review" && (
            <div className="registry-actions mb-4">
              <Button variant="outline" disabled={busy || runs.some(r => r.kind === "central_banks" && ["queued", "running"].includes(r.status))} data-testid="registry-discover-authorities" onClick={() => discover("central_banks")}>
                <Search size={14} />Research central banks
              </Button>
              <span className="registry-note">BIS membership directory · candidate discovery only</span>
            </div>
          )}

          {tab === "review" && (
            <div className="border-b border-[var(--hairline)] pb-5">
              <div className="registry-heading">
                <div>
                  <h2>Find missing government sources</h2>
                  <p className="registry-note">Official-website directory research creates inactive candidates.</p>
                </div>
                <Button variant="outline" disabled={busy || runs.some(r => ["queued", "running"].includes(r.status))} data-testid="registry-start-discovery" onClick={discover}>
                  <Search size={14} />Research countries
                </Button>
              </div>
              {runs[0] && (
                <p data-testid="discovery-run-status" className="registry-note">
                  Latest research: {runs[0].status} · {date(runs[0].created_at)} · {runs[0].candidates_found ?? 0} candidates found
                  {runs[0].reason && ` · ${runs[0].reason}`} <button data-testid="discovery-check-status" className="underline" onClick={load}>Check progress</button>
                </p>
              )}
            </div>
          )}

          <div className="registry-filters">
            <Input data-testid="registry-search" aria-label="Search source names and domains" placeholder="Search sources or domains…" value={filters.search} onChange={e => filter("search", e.target.value)} />
            {select("country", "All countries", (data?.countries || []).map(c => [c.slug, c.name]))}
            {select("subgroup", "All subgroups", Object.entries(data?.groups?.[filters.group]?.subgroups || {}))}
            {select("method", "All collection methods", (data?.methods || []).map(m => [m, m.toUpperCase()]))}
            {tab !== "review" && select("review", "All review states", ["legacy", "candidate", "approved", "rejected"].map(v => [v, v]))}
            {select("health", "All health states", ["healthy", "failing", "no_new_items", "unknown"].map(v => [v, v.replaceAll("_", " ")]))}
          </div>

          {!data ? (
            <Empty id="registry-loading">Loading sources…</Empty>
          ) : !data.sources.length ? (
            <Empty id="registry-empty">No sources match these filters.</Empty>
          ) : (
            <table className="registry-table" data-testid="registry-source-table">
              <thead>
                <tr>
                  <th>Source / organization</th>
                  <th>Group & collection</th>
                  <th>Jurisdiction</th>
                  <th>Review & operation</th>
                  <th>CG Score</th>
                  <th>Collection health</th>
                  <th aria-label="Toggle"></th>
                </tr>
              </thead>
              <tbody>
                {data.sources.map(s => (
                  <tr key={s.source_id} data-testid={`registry-source-${s.source_id}`} className={!s.enabled ? "registry-row-disabled" : ""}>
                    <td>
                      <button className="registry-name" data-testid={`registry-open-${s.source_id}`} onClick={() => setSelected(s.source_id)}>{s.name}</button>
                      <small>{s.duplicate_endpoint_of ? "Retained endpoint alias" : s.language || "Language unreviewed"}</small>
                    </td>
                    <td data-label="Group & collection">{s.subgroup_label}<small>{s.collection_method?.toUpperCase()}</small></td>
                    <td data-label="Jurisdiction">{s.country_name || s.country_slug?.replaceAll("-", " ")}</td>
                    <td data-label="Review & operation">
                      <Pill value={s.review_status} id={`source-review-${s.source_id}`} />
                      <Pill value={s.operational_status} id={`source-operation-${s.source_id}`} />
                    </td>
                    <td data-label="CG Score" data-testid={`source-cg-${s.source_id}`}>
                      <CgBadge grade={s.cg_grade} tier={s.cg_tier} verdict={s.cg_verdict} score={s.cg_score} />
                      {s.cg_checked_at && <small title={`Checked: ${date(s.cg_checked_at)}`}>{s.cg_score ?? "—"}/100</small>}
                    </td>
                    <td data-label="Collection health">
                      <Pill value={s.health} id={`source-health-${s.source_id}`} />
                      <small>{date(s.last_checked_at)}</small>
                    </td>
                    <td data-label="Toggle">
                      <ToggleBtn sourceId={s.source_id} enabled={s.enabled} onToggled={load} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="registry-pagination">
            <span data-testid="registry-result-count">{data?.total || 0} endpoints · Page {page}</span>
            <div className="registry-actions">
              <Button variant="outline" disabled={page === 1 || busy} data-testid="registry-previous" onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="outline" disabled={page * 30 >= (data?.total || 0) || busy} data-testid="registry-next" onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </>
      )}

      {selected && <SourceDetail sourceId={selected} onClose={() => setSelected(null)} onChanged={load} onEdit={s => { setSelected(null); setForm(s); }} />}
      {form && <CandidateForm groups={data?.groups || {}} countries={data?.countries || []} loading={!data} initial={form.source_id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </section>
  );
}
